import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { DraftSettings } from '../components/draft/DraftSettings';
import type { LeagueFormatType } from '../components/LeagueFormatSelector';
import { PageLoader } from '../components/LoadingSpinner';
import NewDraftExperience from '../components/draft/NewDraftExperience';
import { useDraftState } from '../hooks/useDraftState';
import { useCpuDrafting } from '../hooks/useCpuDrafting';
import { useSharedPlayerData } from '../hooks/useSharedPlayerData';
import { createSharedLeagueConfig } from '../utils/sharedLeagueConfig';
import { PageShell } from '../components/ui/PageShell';
import { PageHeader } from '../components/ui/PageHeader';
import { Surface } from '../components/ui/Surface';
import type { ScoringFormat } from '../utils/leagueInsights';
import { assignRosterSlots, formatLabel } from '../utils/draftBoard';
import { PlayerPhoto } from '../components/PlayerPhoto';

const ROUNDS = 16;

const DraftAssistant: React.FC = () => {
  const [draftPosition, setDraftPosition] = useState(6);
  const [teamCount, setTeamCount] = useState(12);
  const [searchQuery, setSearchQuery] = useState('');
  const [scoringFormat, setScoringFormat] = useState<ScoringFormat>('ppr');
  const [leagueFormat, setLeagueFormat] = useState<LeagueFormatType>(() => {
    const stored = localStorage.getItem('leagueFormat');
    return (stored === 'superflex' || stored === '2qb' || stored === 'bestball' || stored === 'dynasty'
      ? stored
      : 'standard') as LeagueFormatType;
  });

  const leagueConfig = useMemo(() => {
    return createSharedLeagueConfig({
      format: leagueFormat,
      scoringFormat,
      teamCount,
      isSuperflex: leagueFormat === 'superflex' || leagueFormat === '2qb',
    });
  }, [leagueFormat, scoringFormat, teamCount]);

  const { players, isLoading: loading, error } = useSharedPlayerData({
    leagueConfig,
    scaleToTen: false,
    scoringFormat,
  });

  const {
    draftState,
    currentTeam,
    round,
    isUserTurn,
    draftPlayer,
    startDraft,
    resetDraft,
  } = useDraftState({
    teamCount,
    draftPosition,
    allPlayers: players,
  });

  const maxPicks = teamCount * ROUNDS;
  const isComplete = draftState.draftStatus === 'active' && draftState.currentPick > maxPicks;

  useCpuDrafting({
    draftStatus: isComplete ? 'ending' : draftState.draftStatus,
    isUserTurn,
    availablePlayers: draftState.availablePlayers,
    currentPick: draftState.currentPick,
    currentTeam,
    round,
    teamCount,
    onCpuPick: draftPlayer,
  });

  useEffect(() => {
    localStorage.setItem('leagueFormat', leagueFormat);
  }, [leagueFormat]);

  useEffect(() => {
    if (draftPosition > teamCount) setDraftPosition(teamCount);
  }, [draftPosition, teamCount]);

  if (loading || !players || players.length === 0) {
    return <PageLoader pageName="Draft Assistant" loadingText="Loading..." />;
  }

  if (error) {
    return (
      <PageShell>
        <PageHeader title="Draft Assistant" />
        <Surface className="mx-auto max-w-lg p-8 text-center">
          <p className="text-zinc-200">Could not load the draft board.</p>
          <p className="mt-2 text-sm text-zinc-400">{error}</p>
        </Surface>
      </PageShell>
    );
  }

  if (isComplete) {
    const assigned = assignRosterSlots(draftState.userRoster, leagueFormat);
    return (
      <PageShell>
        <Helmet>
          <title>Draft complete | Fantasy Fiesta</title>
        </Helmet>
        <PageHeader
          title="Draft complete"
          subtitle={`${formatLabel(leagueFormat)} · ${draftState.userRoster.length} players on your roster`}
        />
        <Surface className="mx-auto max-w-2xl p-6">
          <div className="space-y-3">
            {draftState.userRoster.map((player) => (
              <div key={String(player.id)} className="flex items-center gap-3">
                <PlayerPhoto
                  name={player.name}
                  position={player.position}
                  team={player.team}
                  sleeperId={player.id}
                  espnId={player.espn_id}
                  imageUrl={player.imageUrl}
                  className="h-9 w-9 rounded-full object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-white">{player.name}</p>
                  <p className="text-xs text-zinc-500">
                    {player.position} · {player.team}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {assigned.bench.length === 0 && draftState.userRoster.length === 0 ? (
            <p className="text-sm text-zinc-500">No one landed on your roster.</p>
          ) : null}
          <button
            type="button"
            onClick={resetDraft}
            className="mt-6 rounded-full bg-fiesta-yellow px-5 py-2.5 text-sm font-semibold text-ink"
          >
            Start another mock
          </button>
        </Surface>
      </PageShell>
    );
  }

  if (draftState.draftStatus === 'active') {
    return (
      <NewDraftExperience
        leagueFormat={leagueFormat}
        teamCount={teamCount}
        draftPosition={draftPosition}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        draftState={draftState}
        currentTeam={currentTeam}
        round={round}
        isUserTurn={isUserTurn}
        onDraftPlayer={draftPlayer}
        onLeaveDraft={resetDraft}
      />
    );
  }

  return (
    <PageShell>
      <Helmet>
        <title>Draft Assistant | Fantasy Fiesta</title>
      </Helmet>
      <PageHeader
        title="Draft Assistant"
        subtitle="Set the room, then run a snake mock with picks that fill your holes."
      />

      <div className="mx-auto mb-6 hidden justify-center lg:flex">
        <button
          type="button"
          onClick={startDraft}
          disabled={loading || !teamCount || !draftPosition}
          className="rounded-full bg-fiesta-yellow px-8 py-3 text-lg font-semibold text-ink disabled:bg-zinc-700 disabled:text-zinc-400"
        >
          Start mock
        </button>
      </div>

      <Surface className="mx-auto max-w-3xl p-6 lg:p-8">
        <DraftSettings
          leagueFormat={leagueFormat}
          onFormatChange={setLeagueFormat}
          scoringFormat={scoringFormat}
          onScoringChange={setScoringFormat}
          teamCount={teamCount}
          onTeamCountChange={setTeamCount}
          draftPosition={draftPosition}
          onDraftPositionChange={setDraftPosition}
        />
      </Surface>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-ink/95 p-4 backdrop-blur">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 text-sm">
            <p className="truncate font-medium text-white">
              {formatLabel(leagueFormat)} · {teamCount} teams · seat {draftPosition}
            </p>
            <p className="text-xs text-zinc-500">Snake mock</p>
          </div>
          <button
            type="button"
            onClick={startDraft}
            className="rounded-full bg-fiesta-yellow px-5 py-3 text-sm font-semibold text-ink"
          >
            Start
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default DraftAssistant;
